/* eslint-disable no-nested-ternary */
import React, { useEffect, useState } from "react";

import {
  Button,
  Card,
  Form,
  message,
  Modal,
  Row,
  Select,
  Skeleton,
  Space,
  Typography,
} from "antd";
import PropTypes from "prop-types";

import useDebounce from "../hooks/useDebounce";
import {
  getUserDetailsFromId,
  getUsersWithRefernEarnEnabled,
  tagPartner,
  untagPartner,
} from "../Pages/api";

const ManageTagUntagPartnerModal = ({ visible, onClose, onSave, userId }) => {
  const [form] = Form.useForm();
  const [formValues, setFormValues] = useState({});
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [partners, setPartners] = useState([]);
  const [filters] = useState({
    page: 1,
    pageSize: 25,
  });

  const debouncedSearch = useDebounce({
    value: search,
    callback: () => {},
  });

  const getPartnersList = async () => {
    try {
      const res = await getUsersWithRefernEarnEnabled({
        ...filters,
        search: debouncedSearch,
      });

      if (res?.code === 200) {
        setPartners(res.result.users);
      } else {
        message.error("Something went wrong");
      }
    } catch (err) {
      console.log(err);
      message.error("Something went wrong");
    } finally {
      setLoading(false);
    }
  };

  const getExistingPlanDetails = async () => {
    setLoading(true);
    try {
      const res = await getUserDetailsFromId(userId);

      if (res?.code === 200) {
        setFormValues({
          referredBy: res?.result?.refernEarnParent?._id,
          taggedPartner: res?.result?.refernEarnParent,
        });
      } else {
        message.error(res?.message || "Something went wrong");
      }
    } catch (err) {
      console.log(err);
      if (err?.message) {
        message.error(err.message);
      } else {
        message.error("Something went wrong");
      }
    } finally {
      setLoading(false);
    }
  };

  const tagUntagPartner = async (type, parentId) => {
    setLoading(true);
    try {
      let res;

      if (type === "tag") {
        res = await tagPartner({ parentId }, userId);
      } else {
        res = await untagPartner(userId);
      }

      if (res?.code === 200 || res?.code === 201) {
        message.success(`Partner ${type} successfully`);
        form.resetFields();
        onSave();
      } else {
        message.error(res?.message || "Something went wrong");
      }
    } catch (err) {
      console.log(err);
      message.error(err?.message || "Something went wrong");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (visible && userId) {
      getPartnersList();
      getExistingPlanDetails();
    }
  }, [visible, userId]);

  useEffect(() => {
    if (debouncedSearch) {
      getPartnersList();
    }
  }, [debouncedSearch]);

  return (
    <Modal
      title="Manage Tag Untag Partner"
      visible={visible}
      onCancel={onClose}
      destroyOnClose
      footer={[
        <Row style={{ gap: 16 }}>
          <Button
            key="reset"
            type="default"
            onClick={() => {
              form.resetFields();
              onClose();
            }}
            style={{ flex: 1 }}
          >
            Cancel
          </Button>
          <div style={{ flex: 1, display: "flex" }}>
            <Button
              key="submit"
              type="primary"
              loading={loading}
              disabled={formValues?.taggedPartner?._id}
              onClick={() => {
                form.validateFields().then((values) => {
                  const validatedValues = values;

                  if (validatedValues?.referredBy) {
                    tagUntagPartner("tag", validatedValues?.referredBy);
                  } else {
                    tagUntagPartner("untag");
                  }
                });
              }}
              style={{ flex: 1 }}
            >
              Save
            </Button>
          </div>
        </Row>,
      ]}
    >
      {loading ? (
        <Skeleton active />
      ) : formValues?.taggedPartner ? (
        <Card style={{ margin: "24px 0" }}>
          <Space size="small" direction="vertical">
            <Typography.Title level={5}>
              Tagged Partner Details
            </Typography.Title>
            <Typography style={{}}>
              {formValues?.taggedPartner?.name}
            </Typography>
            <Typography>{formValues?.taggedPartner?.email}</Typography>
            <Typography>{formValues?.taggedPartner?.phone}</Typography>
            <Typography>({formValues?.taggedPartner?.host})</Typography>

            <Typography.Link
              onClick={() => {
                tagUntagPartner("untag");
              }}
            >
              Remove
            </Typography.Link>
          </Space>
        </Card>
      ) : (
        <Form
          form={form}
          layout="vertical"
          onValuesChange={(_, values) =>
            setFormValues((prevState) => ({ ...prevState, ...values }))
          }
        >
          <Form.Item
            style={{ marginTop: "24px" }}
            name="referredBy"
            label="Tagged Partner"
          >
            <Select
              allowClear
              showSearch
              filterOption={false}
              onSearch={(value) => {
                setSearch(value);
              }}
              notFoundContent={null}
              options={partners.map((partner) => ({
                value: partner._id,
                label: `${partner.name} (${partner.email}) - ${partner.host}`,
              }))}
            />
          </Form.Item>
        </Form>
      )}
    </Modal>
  );
};
ManageTagUntagPartnerModal.propTypes = {
  visible: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired,
  userId: PropTypes.string.isRequired,
};

export default ManageTagUntagPartnerModal;
